/**
 * ShadowWalkerSystem — manages Shadow Walker spawning, lifecycle,
 * and interaction with the player.
 *
 * Shadow Walkers patrol maze corridors randomly, can detect the player
 * via line-of-sight, chase them, steal a coin, deal damage, and then
 * dissipate. They appear from level 7+.
 *
 * They are NOT visible through fog of war — only rendered when within
 * the player's fog reveal radius. Ghost mode (Agmuth) lets the player
 * pass through without damage.
 */

import ShadowWalkerEntity from '../entities/ShadowWalkerEntity.js';
import { Events, CellType } from '../utils/constants.js';
import {
  SHADOW_WALKER_MIN_LEVEL,
  SHADOW_WALKER_COUNT_BASE,
  SHADOW_WALKER_COUNT_LEVEL_THRESHOLD,
  SHADOW_WALKER_COUNT_MAX,
  SHADOW_WALKER_MIN_SPAWN_DISTANCE,
  FOG_REVEAL_RADIUS,
  TILE_SIZE,
} from '../config/game-config.js';
import MazeGenerator from './MazeGenerator.js';

export default class ShadowWalkerSystem {
  /**
   * @param {Phaser.Scene} scene             - The GameScene instance.
   * @param {Phaser.Events.EventEmitter} eventBus  - Shared event bus.
   * @param {number[][]} grid                - Maze grid (expanded).
   * @param {number} level                   - Current game level.
   * @param {object} playerRef               - Player sprite.
   * @param {{ col: number, row: number }} startPos - Player start position.
   * @param {import('../systems/HealthSystem.js').default} healthSystem
   * @param {() => boolean} isGhostFn        - Returns true if player is in ghost mode.
   */
  constructor(scene, eventBus, grid, level, playerRef, startPos, healthSystem, isGhostFn) {
    /** @type {Phaser.Scene} */
    this.scene = scene;

    /** @type {Phaser.Events.EventEmitter} */
    this._eventBus = eventBus;

    /** @type {number[][]} */
    this._grid = grid;

    /** @type {number} */
    this._level = level;

    /** @type {object} */
    this._playerRef = playerRef;

    /** @type {import('../systems/HealthSystem.js').default} */
    this._healthSystem = healthSystem;

    /** @type {() => boolean} */
    this._isGhostFn = isGhostFn;

    /** @type {ShadowWalkerEntity[]} Active shadow entities. */
    this._shadows = [];

    /** @type {boolean} */
    this._destroyed = false;

    // Spawn shadows if eligible
    if (level >= SHADOW_WALKER_MIN_LEVEL) {
      this._spawnShadows(startPos);
    }
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /**
   * Called each frame from GameScene.update().
   * @param {number} _time
   * @param {number} delta
   */
  update(_time, delta) {
    if (this._destroyed || !this.scene) return;

    const isGhost = this._isGhostFn ? this._isGhostFn() : false;

    for (let i = this._shadows.length - 1; i >= 0; i--) {
      const shadow = this._shadows[i];
      if (!shadow.active) {
        this._shadows.splice(i, 1);
        continue;
      }

      // Only update/render if within fog reveal radius
      if (this._playerRef) {
        const dist = Math.abs(shadow.gridCol - this._playerRef.gridCol) +
                     Math.abs(shadow.gridRow - this._playerRef.gridRow);
        const visible = dist <= FOG_REVEAL_RADIUS * 2 + 2;
        shadow.setVisible(visible);
        if (shadow._eyeGfx) shadow._eyeGfx.setVisible(visible);
      }

      const result = shadow.tick(delta, isGhost);

      if (result.hit) {
        this._onShadowHit(shadow, result.damage, result.coinsStolen);
      }
    }
  }

  /**
   * Get the number of active shadows.
   * @returns {number}
   */
  get activeShadowCount() {
    return this._shadows.filter(s => s.active).length;
  }

  /**
   * Destroy all shadows and clean up.
   */
  destroy() {
    this._destroyed = true;

    for (const shadow of this._shadows) {
      if (shadow.active) {
        shadow.destroy();
      }
    }
    this._shadows = [];

    this.scene = null;
    this._playerRef = null;
    this._eventBus = null;
    this._healthSystem = null;
    this._isGhostFn = null;
    this._grid = null;
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /**
   * Spawn shadow walkers for this level.
   * @private
   * @param {{ col: number, row: number }} startPos
   */
  _spawnShadows(startPos) {
    const count = this._level >= SHADOW_WALKER_COUNT_LEVEL_THRESHOLD
      ? SHADOW_WALKER_COUNT_MAX
      : SHADOW_WALKER_COUNT_BASE;

    const pathCells = MazeGenerator.getPathCells(this._grid);

    // Filter cells far enough from player start
    const candidates = pathCells.filter(c => {
      const dist = Math.abs(c.col - startPos.col) + Math.abs(c.row - startPos.row);
      return dist >= SHADOW_WALKER_MIN_SPAWN_DISTANCE * 2;
    });

    if (candidates.length === 0) return;

    // Shuffle candidates
    for (let i = candidates.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
    }

    for (let i = 0; i < Math.min(count, candidates.length); i++) {
      const { col, row } = candidates[i];
      const shadow = new ShadowWalkerEntity(this.scene, col, row, this._grid, this._playerRef);
      this._shadows.push(shadow);
      this._eventBus.emit(Events.SHADOW_WALKER_SPAWNED);
    }
  }

  /**
   * Handle a shadow hitting the player.
   * The shadow does NOT despawn — it retreats and will attack again.
   * @private
   * @param {ShadowWalkerEntity} shadow
   * @param {number} damage
   * @param {number} coinsStolen
   */
  _onShadowHit(shadow, damage, coinsStolen) {
    // Apply damage
    if (this._healthSystem) {
      this._healthSystem.takeDamage(damage);
    }

    // Emit events
    this._eventBus.emit(Events.SHADOW_WALKER_HIT, { damage, coinsStolen });

    // Red/purple flash on player
    if (this._playerRef && this._playerRef.active) {
      this._playerRef.setTint(0x8800aa);
      if (this.scene) {
        this.scene.time.delayedCall(300, () => {
          if (this._playerRef && this._playerRef.active) {
            this._playerRef.clearTint();
          }
        });
      }
    }

    // Brief camera shake to emphasise the hit
    if (this.scene && this.scene.cameras && this.scene.cameras.main) {
      this.scene.cameras.main.shake(200, 0.005);
    }
  }
}
